/**
 * hooks.ts · TanStack Query para campañas sociales.
 *
 * El selector de «toque vencido» vive acá y en un solo lugar: lo consumen la
 * bandeja, el contador de la navegación y el indicador de la fila de campaña.
 * Con tres consultas distintas, el badge y la bandeja terminan discrepando.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useSupabase } from '@/hooks/useSupabase';
import type { SocialCampaignOutcome } from '@/types/database.types';

import { campaignsKeys } from '../hooks';
import {
  closeCampaign,
  editTouchBody,
  findCampaignLead,
  generateSocialReplies,
  listChannelRules,
  listDueTouches,
  listTouches,
  listTouchesForCampaigns,
  markConnected,
  markReplied,
  markTouchSent,
  markTouchSkipped,
  rescheduleMany,
  rescheduleTouch,
  updateChannelRule,
} from './api';
import type { ChannelRule, DueTouch, SocialTouch } from './types';

export const socialKeys = {
  all: ['social'] as const,
  touches: (campaignId: string) => [...socialKeys.all, 'touches', campaignId] as const,
  touchesMany: (ids: string[]) => [...socialKeys.all, 'touches-many', ids] as const,
  due: () => [...socialKeys.all, 'due'] as const,
  lead: (campaignId: string) => [...socialKeys.all, 'lead', campaignId] as const,
  rules: () => [...socialKeys.all, 'rules'] as const,
};

// ── Lecturas ────────────────────────────────────────────────────────────────

export function useSocialTouches(campaignId: string | undefined) {
  return useQuery<SocialTouch[]>({
    queryKey: campaignId ? socialKeys.touches(campaignId) : [...socialKeys.all, 'unset'],
    queryFn: () => listTouches(campaignId as string),
    enabled: !!campaignId,
    staleTime: 15_000,
  });
}

export function useSocialTouchesForCampaigns(campaignIds: string[]) {
  return useQuery<SocialTouch[]>({
    queryKey: socialKeys.touchesMany(campaignIds),
    queryFn: () => listTouchesForCampaigns(campaignIds),
    enabled: campaignIds.length > 0,
    staleTime: 15_000,
  });
}

/**
 * 🔴 LA fuente de «vencido». No hay otra.
 *
 * Sale de `core.v_social_touches_due`, que filtra por estado de campaña Y por
 * resultado. Cualquier pantalla que quiera saber qué está vencido consume este
 * hook — nunca recalcula el predicado por su cuenta.
 */
export function useDueTouches() {
  return useQuery<DueTouch[]>({
    queryKey: socialKeys.due(),
    queryFn: listDueTouches,
    staleTime: 30_000,
    /*
     * Se refresca sola: la vista depende de `now()`, así que un toque vence sin
     * que cambie ninguna fila. Sin esto, con la pestaña abierta la bandeja se
     * quedaba con lo que había al cargar (el default global del cliente es
     * `refetchOnWindowFocus: false`) y un toque que vencía a media mañana no
     * aparecía hasta recargar. Realtime no sirve acá por el mismo motivo: no
     * hay evento que escuchar.
     */
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Cuántos toques vencidos hay, para el indicador de la navegación.
 *
 * Deriva del MISMO query que la bandeja: por construcción no pueden dar
 * números distintos. `undefined` cuando todavía no cargó — el badge no se
 * renderiza, en vez de mostrar un cero que después salta.
 */
export function useDueTouchCount(): number | undefined {
  const { data } = useDueTouches();
  return data?.length;
}

export function useChannelRules() {
  return useQuery<ChannelRule[]>({
    queryKey: socialKeys.rules(),
    queryFn: listChannelRules,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateChannelRule() {
  const qc = useQueryClient();
  return useMutation<
    ChannelRule,
    Error,
    { id: string; patch: Parameters<typeof updateChannelRule>[1] }
  >({
    mutationFn: ({ id, patch }) => updateChannelRule(id, patch),
    onSuccess: (fila) => {
      void qc.invalidateQueries({ queryKey: socialKeys.rules() });
      toast.success(
        fila.verified_at
          ? 'Regla guardada y verificada'
          : 'Regla guardada · quedó sin verificar',
      );
    },
    onError: (e) => toast.error('No se pudo guardar la regla', { description: e.message }),
  });
}

// ── Marcas del vendedor ─────────────────────────────────────────────────────

/** Invalida todo lo que un cambio de toque puede haber movido. */
function useInvalidarToques() {
  const qc = useQueryClient();
  return (campaignId?: string) => {
    void qc.invalidateQueries({ queryKey: socialKeys.all });
    void qc.invalidateQueries({ queryKey: campaignsKeys.lists() });
    if (campaignId) {
      void qc.invalidateQueries({ queryKey: campaignsKeys.detail(campaignId) });
    }
  };
}

export function useMarkTouchSent() {
  const { user } = useSupabase();
  const invalidar = useInvalidarToques();
  return useMutation<SocialTouch, Error, { touchId: string; campaignId?: string }>({
    mutationFn: ({ touchId }) => {
      if (!user) throw new Error('Sin sesión');
      return markTouchSent(touchId, user.id);
    },
    onSuccess: (_row, vars) => invalidar(vars.campaignId),
    onError: (e) => toast.error('No se pudo marcar como enviado', { description: e.message }),
  });
}

export function useMarkTouchSkipped() {
  const { user } = useSupabase();
  const invalidar = useInvalidarToques();
  return useMutation<SocialTouch, Error, { touchId: string; campaignId?: string }>({
    mutationFn: ({ touchId }) => {
      if (!user) throw new Error('Sin sesión');
      return markTouchSkipped(touchId, user.id);
    },
    onSuccess: (_row, vars) => invalidar(vars.campaignId),
    onError: (e) => toast.error('No se pudo saltear', { description: e.message }),
  });
}

export function useEditTouch() {
  const invalidar = useInvalidarToques();
  return useMutation<SocialTouch, Error, { touchId: string; body: string; campaignId?: string }>({
    mutationFn: ({ touchId, body }) => editTouchBody(touchId, body),
    onSuccess: (_row, vars) => {
      invalidar(vars.campaignId);
      toast.success('Texto guardado');
    },
    onError: (e) => toast.error('No se pudo guardar', { description: e.message }),
  });
}

export function useRescheduleTouch() {
  const invalidar = useInvalidarToques();
  return useMutation<
    void,
    Error,
    { cambios: Array<{ id: string; scheduled_for: string }>; campaignId?: string }
  >({
    mutationFn: async ({ cambios }) => {
      if (cambios.length === 1) {
        await rescheduleTouch(cambios[0].id, cambios[0].scheduled_for);
        return;
      }
      await rescheduleMany(cambios);
    },
    onSuccess: (_v, vars) => {
      invalidar(vars.campaignId);
      toast.success('Fechas actualizadas');
    },
    onError: (e) => toast.error('No se pudo reprogramar', { description: e.message }),
  });
}

// ── Resultado de la campaña ─────────────────────────────────────────────────

export function useMarkConnected() {
  const invalidar = useInvalidarToques();
  return useMutation<unknown, Error, string>({
    mutationFn: (campaignId) => markConnected(campaignId),
    onSuccess: (_r, campaignId) => {
      invalidar(campaignId);
      toast.success('Marcado: aceptó la solicitud');
    },
    onError: (e) => toast.error('No se pudo marcar', { description: e.message }),
  });
}

/** La conversación que originó la campaña, si ya existe. */
export function useCampaignLead(campaignId: string | undefined) {
  return useQuery<string | null>({
    queryKey: campaignId ? socialKeys.lead(campaignId) : [...socialKeys.all, 'unset'],
    queryFn: () => findCampaignLead(campaignId as string),
    enabled: !!campaignId,
    staleTime: 15_000,
  });
}

export function useMarkReplied() {
  const invalidar = useInvalidarToques();
  return useMutation<string, Error, { campaignId: string; firstMessage: string }>({
    mutationFn: ({ campaignId, firstMessage }) => markReplied(campaignId, firstMessage),
    onSuccess: (_id, vars) => {
      invalidar(vars.campaignId);
      toast.success('Conversación creada', {
        description: 'El mensaje quedó registrado a mano en el historial.',
      });
    },
    onError: (e) =>
      toast.error('No se pudo crear la conversación', { description: e.message }),
  });
}

export function useCloseCampaign() {
  const invalidar = useInvalidarToques();
  return useMutation<
    void,
    Error,
    { campaignId: string; outcome: Extract<SocialCampaignOutcome, 'no_reply' | 'discarded'> }
  >({
    mutationFn: ({ campaignId, outcome }) => closeCampaign(campaignId, outcome),
    onSuccess: (_v, vars) => {
      invalidar(vars.campaignId);
      toast.success(
        vars.outcome === 'no_reply'
          ? 'Cerrada sin respuesta y archivada'
          : 'Descartada y archivada',
      );
    },
    onError: (e) => toast.error('No se pudo cerrar la campaña', { description: e.message }),
  });
}

// ── Sugerencias en leads sociales ───────────────────────────────────────────

export function useSocialReplySuggestions() {
  return useMutation({
    mutationFn: (conversationId: string) => generateSocialReplies(conversationId),
    onError: (e: Error) =>
      toast.error('No se pudieron generar sugerencias', { description: e.message }),
  });
}
